import React, { Component } from 'react';
import { Route, Switch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as sessionActions from '../actions/sessionActions';
import * as profileActions from '../actions/profileActions';
import * as appActions from '../actions/appActions';
import * as policyActions from '../actions/policyActions';
import { push } from 'connected-react-router';
import { sessionSelector, authSelector, appSelector, profileSelector, policySelector, claimSelector } from '../selectors';
import { Segment, Visibility, Confirm, Dimmer, Loader } from 'semantic-ui-react';
import { AppHeader, AppFooter, MakePaymentHeader } from '../components/sections';
import SessionTimer from './SessionTimer';
import { scroller as scroll, animateScroll } from 'react-scroll';
import HomePage from './HomePage';
import AdminPage from './AdminPage';
import AdminImportUsersPage from './AdminImportUsersPage';
import LoginPage from './auth/LoginPage';
import LogoutPage from './auth/LogoutPage';
import RegistrationPage from './auth/RegistrationPage';
import ConfirmEmail from './auth/ConfirmEmailPage';
import ForgotPasswordPage from './auth/ForgotPasswordPage';
import ResetPasswordPage from './auth/ResetPasswordPage';
import ConfirmEmailChangePage from './auth/ConfirmEmailChangePage';
import LegacyUserPage from './auth/LegacyUserPage';
import UserProfilePage from './profile/UserProfilePage';
import PrivateRoute from './PrivateRoute';
import OneTimePaymentPage from './OneTimePaymentPage';
import * as claimActions from '../actions/claimActions';
import PolicyUpdatePage from './PolicyUpdatePage';
import ReportClaimPage from './ReportClaimPage';
import Advertisements from '../components/advertisements/Advertisements';

class App extends Component {
	constructor(props) {
		super(props);
		this.state = {
			timeout: 1000 * 60 * 19, //19 min
			sessionOpen: false,
			logoutTimer: 1000 * 60 * 20, //20 min
			headerFixed: false,
			showWelcome: true,
			showPayments: false,
			showClaims: false,
			showAgency: false,
			showPolicies: false,
			showProfile: false,
			showLogout: false,
			showAdvertisement: true,
		};
		this.linkClick = this.linkClick.bind(this);
		this.onIdle = this.onIdle.bind(this);
		this.onLogout = this.onLogout.bind(this);
		this.onCancelLogout = this.onCancelLogout.bind(this);
		this.onConfirmLogout = this.onConfirmLogout.bind(this);
		this.onVisibilityUpdate = this.onVisibilityUpdate.bind(this);
		this.onPolicyRetry = this.onPolicyRetry.bind(this);
		this.onCloseAdvertisement = this.onCloseAdvertisement.bind(this);
		this.onToggleDontShow = this.onToggleDontShow.bind(this);
		this.onGoToEDelivery = this.onGoToEDelivery.bind(this);

		const { actions } = props;
		actions.getSystemInformation();
	}

	UNSAFE_componentWillMount() {
		this.setState({ width: window.innerWidth });
	}

	componentDidUpdate(prevProps, prevState) {
		const { session } = this.props;

		if (this.state.sessionOpen !== prevState.sessionOpen) return;

		if (session.authenticated && !prevProps.authenticated) this.refs.sessionTimer.reset();

		if (!session.authenticated) this.refs.sessionTimer.stop();
	}

	onVisibilityUpdate(e, { calculations }) {
		const { topPassed, pixelsPassed, width } = calculations;
		if (width > 767 && topPassed !== this.state.headerFixed) {
			this.setState({ headerFixed: topPassed });
		}

		if (pixelsPassed <= 64 && !this.state.showWelcome) {
			this.setState({
				showWelcome: true,
				showPayments: false,
				showClaims: false,
				showAgency: false,
				showProfile: false,
				showPolicies: false,
				showLogout: false,
			});
		}

		if (pixelsPassed >= 128 && this.state.showWelcome) {
			this.setState({
				showWelcome: false,
				showPayments: true,
				showClaims: true,
				showAgency: true,
				showProfile: true,
				showLogout: true,
				showPolicies: true,
			});
		}
	}

	linkClick(e) {
		e.preventDefault();
		const { pathname, search, hash } = e.target.href ? e.target : e.target.parentNode;
		const url = pathname + search + hash;
		this.props.actions.push(url);

		setTimeout(() => {
			const element = url.replace('/', '');
			if (element.length && ['payments', 'claims', 'policies', 'agency', 'autoidcards'].includes(element)) {
				return scroll.scrollTo(element, {
					duration: 500,
					delay: 100,
					smooth: true,
					offset: -100,
				});
			}
			if (element.length === 0) {
				return animateScroll.scrollToTop({
					duration: 500,
					delay: 100,
					smooth: true,
				});
			}
		}, 75);
	}

	onIdle() {
		this.setState({
			sessionOpen: true,
		});
	}

	onLogout() {
		this.props.actions.push('/logout');
		this.setState({
			sessionOpen: false,
		});
	}

	onConfirmLogout() {
		this.props.actions.push('/logout');
		this.setState({
			sessionOpen: false,
		});
	}

	onCancelLogout() {
		this.refs.sessionTimer.reset();
		this.setState({
			sessionOpen: false,
		});
	}

	onCloseAdvertisement() {
		this.setState({ showAdvertisement: false });
	}

	onPolicyRetry() {
		const { actions, profile } = this.props;
		actions.getPolicies(profile.policies);
	}

	onToggleDontShow(e, { checked }) {
		const { actions, profile } = this.props;
		actions.updateDontShowAd({ ...profile, dontShowAdvertisement: checked });
	}

	onGoToEDelivery() {
		const { actions } = this.props;
		actions.push('/profile/alerts'); 
	}

	render() {
		const { session, app, profile, actions } = this.props;
		const { headerFixed, showPayments, showAgency, showClaims, showProfile, showPolicies, showWelcome, showLogout, width } = this.state;
		const profileOverride = this.props.location.pathname.startsWith('/profile');

		return (
			<SessionTimer ref='sessionTimer' element={document} sessionTimeoutAction={this.onLogout} preSessionTimeoutAction={this.onIdle} sessionTimeout={this.state.logoutTimer} preSessionTimeout={this.state.timeout} format='MM-DD-YYYY HH:MM:ss.SSS'>
				<div className='app'>
					<Confirm open={this.state.sessionOpen} confirmButton='Continue' cancelButton='Log Out' content='You are about to be logged out.' onCancel={this.onConfirmLogout} onConfirm={this.onCancelLogout} />
					<Confirm open={this.props.app.retryPolicy} confirmButton='Retry?' cancelButton={null} content='There was a problem fetching policy data.' onConfirm={this.onPolicyRetry} />

					{session.authenticated && this.props.location.pathname === '/' && <Advertisements actions={actions} profile={profile} advertisement={app.advertisement} onGoToEDelivery={this.onGoToEDelivery} />}
					
					<Dimmer active={!!app.dimmer} page>
						<Loader indeterminate size='massive' content={app.dimmer} />
					</Dimmer>

					<Segment basic={!headerFixed} className={width > 767 ? 'fixed' : ''}>
						{this.props.location.pathname === '/makepayment' ? (
							<MakePaymentHeader />
						) : (
							<AppHeader
								mobileShow={this.props.location.pathname !== '/' && this.props.location.pathname !== '/login'}
								session={session}
								linkClick={this.linkClick}
								name={profile.name || ''}
								show={{
									showWelcome: profileOverride ? false : showWelcome,
									showPayments: profileOverride ? true : showPayments,
									showAgency: profileOverride ? true : showAgency,
									showClaims: profileOverride ? true : showClaims,
									showProfile: profileOverride ? true : showProfile,
									showPolicies: profileOverride ? true : showPolicies,
									showLogout: profileOverride ? true : showLogout,
								}}
							/>
						)}
					</Segment>
					<Visibility onUpdate={this.onVisibilityUpdate}>
						<div className={`push ${this.props.location.pathname === '/makepayment' ? 'payment' : ''}`}>
							<Switch>
								<PrivateRoute exact path='/' component={HomePage} />
								<PrivateRoute path='/payments' component={HomePage} />
								<PrivateRoute path='/claims' component={HomePage} />
								<PrivateRoute path='/agency' component={HomePage} />
								<PrivateRoute path='/policies' component={HomePage} />
								<PrivateRoute path='/paynow' component={HomePage} />
								<PrivateRoute path='/autoidcards' component={HomePage} />
								<PrivateRoute path='/help' component={HomePage} />
								<PrivateRoute path='/:policyNumber/documents' component={HomePage} />
								<PrivateRoute path='/:policyNumber/paynow' component={HomePage} />
								<PrivateRoute path='/profile' component={UserProfilePage} />

								<PrivateRoute path='/admin' component={AdminPage} />
								<PrivateRoute path='/admin/import' component={AdminImportUsersPage} />
								<PrivateRoute path='/policy/update/:policyNumber' component={PolicyUpdatePage} />
								<PrivateRoute path='/report-claim' component={ReportClaimPage} />

								<Route path='/login' component={LoginPage} />
								<Route path='/logout' component={LogoutPage} />
								<Route path='/confirmemail' component={ConfirmEmail} />
								<Route path='/registration' component={RegistrationPage} />
								<Route path='/forgot' component={ForgotPasswordPage} />
								<Route path='/resetpassword' component={ResetPasswordPage} />
								<Route path='/changeemail' component={ConfirmEmailChangePage} />
								<Route path='/legacyuser' component={LegacyUserPage} />
								<Route path='/makepayment' component={OneTimePaymentPage} /> 
							</Switch>
						</div>
						<AppFooter location={this.props.location} />
					</Visibility>
				</div>
			</SessionTimer>
		);
	}
}

App.propTypes = {
	actions: PropTypes.object.isRequired,
	app: PropTypes.object,
	session: PropTypes.object.isRequired,
	auth: PropTypes.object.isRequired,
	profile: PropTypes.object.isRequired,
	policy: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
	return {
		app: appSelector(state),
		auth: authSelector(state),
		session: sessionSelector(state),
		profile: profileSelector(state),
		policy: policySelector(state),
		claim: claimSelector(state),
	};
};

const mapDispatchToProps = (dispatch) => ({
	actions: {
		...bindActionCreators({ push }, dispatch),
		...bindActionCreators(policyActions, dispatch),
		...bindActionCreators(sessionActions, dispatch),
		...bindActionCreators(profileActions, dispatch),
		...bindActionCreators(appActions, dispatch),
		...bindActionCreators(claimActions, dispatch),
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
